'use client';

/**
 * The layer stack: one owner for dismissal and modal depth across every floating layer (docs/THEME.md 1.7, 2.10).
 * Moved here from design-system/primitives/layers/layerStack.ts (that path re-exports this module).
 *
 * ── Why this must exist before any layer component ───────────────────────────
 * If each tooltip, popover, menu and dialog attaches its own document listeners,
 * then: Escape closes ALL of them at once instead of the top one; an outside
 * press dismisses the wrong layer; and a nested layer closing releases its
 * parent's scroll lock. Those bugs are not fixable inside the individual
 * components — dismissal is inherently a global, ordered concern.
 *
 * ── Window, capture phase ────────────────────────────────────────────────────
 * Radix (Dialog, Select, DropdownMenu) listens for Escape on the DOCUMENT in the capture phase, and the dialog that
 * opened first registered first, so a document listener of ours would run after it and the Escape meant for a popover
 * would close the dialog that hosts it. The window's capture phase runs before the document's, so the top layer of
 * this stack always sees the key first and stops it.
 *
 * ── Modal depth ──────────────────────────────────────────────────────────────
 * Every modal (the shared Dialog in every variant, AlertDialog, confirm) registers its depth here. A modal opened on
 * top of another gets the next depth, and its z band comes from `modalBand(depth)`: the existing Dialog values
 * 10000/10001 (depth 1) and 100100/100101 (depth 2), then +100 per depth. Legacy variants register their FIXED depth
 * (DialogContent 1, DialogContentNested 2) so a migrated modal opened over them lands above them, while their own z
 * values stay exactly as they were.
 */

/**
 * Owned compatibility bands for surfaces that have not moved into a modal owner slot yet.
 * Keep the existing values: collapsing these onto page-popup (1000) or modal-popup (10002)
 * changes paint order against sibling page popups, alerts and legacy overlays.
 * Modal backdrops/content continue to come from modalBand(); these are their existing neighbours.
 */
export const LAYER_BANDS = {
  floatingChrome: 1100, // HeaderClient and the non-modal debug window, above page-owned popups.
  authStatus: 9000,    // Bottom auth banner, below the recording frame and every modal.
  flyingCard: 9998,    // Page animation, below the recording frame and modal-1 backdrop.
  recording: 9999,
  mediaPreview: 99999, // Video tips stay above legacy modal-1 popups, below modal-2.
  demoCursor: 99999,
  demoRipple: 99998,
} as const;

export type CloseReason =
  | 'escape'
  | 'explicit'
  | 'select'
  | 'outside-pointer'
  | 'focus-out'
  | 'trigger-toggle'
  | 'unmount';

/**
 * Whether a close reason should send focus back to the trigger.
 *
 * This table is the whole point. "Dismiss when focus leaves" and "always restore
 * the trigger" cannot both be true: on Tab-out, restoring would bounce focus
 * backwards and turn a non-modal layer into a trap. Restoration is only correct
 * when the user did NOT choose somewhere else to be.
 */
export function shouldRestoreFocus(reason: CloseReason): boolean {
  switch (reason) {
    case 'escape':
    case 'explicit':
    case 'select':
      return true;
    // The user has already chosen a new focus target. Moving them is hostile.
    case 'outside-pointer':
    case 'focus-out':
      return false;
    // The trigger already owns focus; re-focusing would be a no-op at best.
    case 'trigger-toggle':
    case 'unmount':
      return false;
  }
}

export interface Layer {
  readonly id: string;
  /** Non-modal layers dismiss on outside press; modal ones do not. */
  readonly modal: boolean;
  /** Excluded from outside-press detection so the trigger owns toggling. */
  readonly trigger: HTMLElement | null;
  readonly content: HTMLElement | null;
  close(reason: CloseReason): void;
  /**
   * Optional: true when this Escape belongs to something the layer hosts (a Radix Select list inside a popup panel),
   * which closes itself on the same key. The stack then neither stops the event nor closes this layer.
   */
  yieldsEscape?(event: KeyboardEvent): boolean;
}

const stack: Layer[] = [];
let listening = false;

export function topLayer(): Layer | undefined {
  return stack[stack.length - 1];
}

/** Whether `inner`'s trigger sits inside `outer`'s content (a layer opened from inside another one). */
function opensFrom(inner: Layer, outer: Layer): boolean {
  const trigger = inner.trigger;
  const content = outer.content;
  return !!trigger && !!content && trigger !== content && content.contains(trigger);
}

/**
 * Push a layer. Normally it goes on top (opened later is on top). A layer opened from INSIDE one that is already on the
 * stack is its child and must stay above it; React runs a child's effects before its parent's, so when both mount in
 * the same commit the child pushes first. The parent then goes BELOW its first child already on the stack.
 */
export function pushLayer(layer: Layer): () => void {
  const firstChild = stack.findIndex((other) => opensFrom(other, layer));
  if (firstChild >= 0) stack.splice(firstChild, 0, layer);
  else stack.push(layer);
  ensureListeners();
  return () => {
    const i = stack.indexOf(layer);
    if (i >= 0) stack.splice(i, 1);
    if (stack.length === 0) teardownListeners();
  };
}

/** Close non-modal layers from the top down. A synthetic outside press can exclude the entry it already closed. */
export function closeNonModalLayers(reason: CloseReason = 'explicit', alreadyClosed?: Layer): void {
  for (const layer of [...stack].reverse()) if (!layer.modal && layer !== alreadyClosed) layer.close(reason);
}

function onPointerDown(event: PointerEvent) {
  // Primary button only. A right-click opens a context menu; closing the layer
  // underneath it is not what the user asked for.
  if (!event.isPrimary || event.button !== 0) return;

  const layer = topLayer();
  if (!layer || layer.modal) return;

  // composedPath, not event.target: the press may originate inside a shadow root
  // or a portalled subtree, where target is retargeted.
  const path = event.composedPath();

  // The trigger owns toggling. Without this exclusion you get the classic
  // double-toggle: the document listener closes, then the trigger's click
  // reopens, and the layer appears not to respond at all.
  if (layer.trigger && path.includes(layer.trigger)) return;
  if (layer.content && path.includes(layer.content)) return;

  // Never preventDefault — whatever the user pressed outside should still work.
  layer.close('outside-pointer');
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || event.isComposing) return;
  const layer = topLayer();
  if (!layer) return;
  if (layer.yieldsEscape?.(event)) return;
  event.stopPropagation();
  layer.close('escape');
}

function ensureListeners() {
  if (listening) return;
  listening = true;
  // Window, capture phase: before Radix's document-capture handlers (see the header) and before any layer could
  // swallow the event with stopPropagation; propagation must never be how layer ownership is decided.
  window.addEventListener('pointerdown', onPointerDown, true);
  window.addEventListener('keydown', onKeyDown, true);
}

function teardownListeners() {
  if (!listening) return;
  listening = false;
  window.removeEventListener('pointerdown', onPointerDown, true);
  window.removeEventListener('keydown', onKeyDown, true);
}

/* ── Modal depth ─────────────────────────────────────────────────────────── */

export interface ModalBand {
  readonly depth: number;
  readonly backdrop: number;
  readonly content: number;
}

/** The z band of modal depth n (docs/THEME.md 1.7): 10000/10001, 100100/100101, then +100 per depth. */
export function modalBand(depth: number): ModalBand {
  const d = Math.max(1, Math.floor(depth));
  const backdrop = d === 1 ? 10000 : 100100 + (d - 2) * 100;
  return { depth: d, backdrop, content: backdrop + 1 };
}

interface ModalEntry { depth: number; parent: symbol | null }
const modals = new Map<symbol, ModalEntry>();

/** Whether modal `key` is `ancestor` itself or sits somewhere inside it (walks the registered parent links). */
function inSubtree(key: symbol, ancestor: symbol): boolean {
  let at: symbol | null = key;
  for (let hops = 0; at && hops < 64; hops++) {
    if (at === ancestor) return true;
    at = modals.get(at)?.parent ?? null;
  }
  return false;
}

/**
 * The depth the NEXT modal would get: one above the deepest open modal (1 when none is open). Pure read.
 * `self` is the asking modal's own key: modals INSIDE it are not counted, because they stack above it by construction.
 * (Three nested dialogs that open in one commit register child-first; without this the middle one counted its own child
 * and took depth 4 instead of 2.)
 */
export function nextModalDepth(minDepth = 1, self?: symbol): number {
  let deepest = 0;
  for (const [key, entry] of modals) {
    if (self && inSubtree(key, self)) continue;
    deepest = Math.max(deepest, entry.depth);
  }
  return Math.max(deepest + 1, minDepth);
}

/**
 * Register an open modal. `fixedDepth` is for the legacy Dialog variants, whose z values never change: they are
 * counted at the depth their z already is. Popups that are open when a modal opens are closed first (2.10: a new
 * modal closes an ancestor's open popup, focus not restored to it).
 */
export function registerModal(opts: { fixedDepth?: number; minDepth?: number; key?: symbol; parent?: symbol | null } = {}): { depth: number; release: () => void } {
  const key = opts.key ?? Symbol('modal');
  const depth = opts.fixedDepth ?? nextModalDepth(opts.minDepth, key);
  closeNonModalLayers('explicit');
  modals.set(key, { depth, parent: opts.parent ?? null });
  return { depth, release: () => { modals.delete(key); } };
}

/** The depths of the open modals (tests and the gallery). */
export function openModalDepths(): number[] {
  return [...modals.values()].map((m) => m.depth).sort((a, b) => a - b);
}

/**
 * Reference-counted scroll lock.
 *
 * Counted because closing a nested layer must not unlock the page while its
 * parent is still open. Prior inline styles are captured and restored so we do
 * not clobber whatever the app had set.
 */
let lockCount = 0;
let savedScrollY = 0;
let saved: Record<string, string> = {};

export function lockScroll() {
  if (++lockCount !== 1) return;
  const body = document.body;
  savedScrollY = window.scrollY;
  const gutter = window.innerWidth - document.documentElement.clientWidth;

  saved = {
    position: body.style.position,
    top: body.style.top,
    width: body.style.width,
    overflow: body.style.overflow,
    paddingRight: body.style.paddingRight,
  };

  // position:fixed rather than overflow:hidden — iOS Safari ignores the latter.
  body.style.position = 'fixed';
  body.style.top = `${-savedScrollY}px`;
  body.style.width = '100%';
  body.style.overflow = 'hidden';
  if (gutter > 0) {
    const existing = parseFloat(getComputedStyle(body).paddingRight) || 0;
    body.style.paddingRight = `${existing + gutter}px`;
  }
}

export function unlockScroll() {
  if (--lockCount !== 0) return;
  Object.assign(document.body.style, saved);
  window.scrollTo({ top: savedScrollY, behavior: 'instant' as ScrollBehavior });
}

/** Test-only. Never call from product code. */
export function __resetLayerStack() {
  stack.length = 0;
  teardownListeners();
  lockCount = 0;
  modals.clear();
}
